/**
 * One thing to do, with the rest stacked visibly behind it.
 *
 * The ranking is in constants/nextAction.js and none of it is here. This file
 * is the card: what it looks like, and how a coach moves through the stack.
 *
 * What Coleman asked of it, and where each one lives:
 *
 *   THE STACK SHOWS ITS CARDS. The cards behind are strips under this one
 *   with their titles on them. The first is Next. Tapping any brings it
 *   forward.
 *
 *   YOU CAN GO BACK. The stack is a ring you move through in both
 *   directions: a back arrow on the card once you have moved, a swipe either
 *   way on a phone, the arrow keys on a desk. Looking never removes a card;
 *   a card leaves when the thing it asks for is done.
 *
 *   MOTION THAT EASES. Moving through the stack slides the new card in from
 *   the side it came from. Hover lifts the card a little. Every transition
 *   is on one curve (EASE) so nothing snaps. The first version set
 *   `transition: none` inline whenever it was not springing back from a
 *   swipe, which silently killed every hover transition in the stylesheet;
 *   that is what "rigid" was.
 *
 *   A FACE ONLY WHEN IT IS REAL. A call counts down, live, to the second. A
 *   masterclass wears its actual cover, with a progress bar on it when it is
 *   half read. New things count up to how many. Steps and units fill a ring.
 *   Anything else has no picture at all: the icon tiles and the generated
 *   gold-grid art were decoration, and read as filler.
 *
 *   URGENT LOOKS URGENT, AND NOTHING ELSE DOES. A failed payment and a call
 *   inside half an hour get the gold border and a live dot. The button is
 *   the one gold fill (playbook/builderUi.js) on every other card.
 *
 * Gesture discipline is lifted from DrillStack: threshold before capture so
 * a tap stays a tap, primary pointer only, pointer capture so a flick that
 * leaves the card still ends here, and the click after a drag swallowed.
 *
 * No em dashes.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CalendarClock, CreditCard, UserRound, Compass, GraduationCap,
  MessageCircleQuestion, Sparkles, ArrowRight, ArrowLeft, Clipboard, Target, Award, Gift } from "lucide-react";
import { GOLD } from "../constants/theme";
import { haptic } from "../../utils/nativeBridge";

/** Past this fraction of the card's width, the thumb meant it. */
const SWIPE_FRACTION = 0.22;

/** A tap is not a drag until it has moved this far. */
const DRAG_START = 8;

/** Strips drawn under the card. More than two is a pile, not a preview. */
const STRIPS = 2;

/** The one curve. Fast out of the gate, soft landing. */
const EASE = "cubic-bezier(.2,.8,.2,1)";

const R = { md: 10, lg: 12, xl: 14, pill: 999 };

const KIND_ICON = {
  call: CalendarClock, billing: CreditCard, setup: UserRound,
  "start-here": Compass, resume: GraduationCap, qa: MessageCircleQuestion,
  unexplored: Sparkles, "new-masterclass": GraduationCap, "new-xo": Clipboard,
  "new-drills": Target, cert: Award, drop: Gift,
};

const reducedMotion = () => {
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return false; }
};

/* ── Faces ────────────────────────────────────────────────────────────── */

/** Ticks once a second. Each digit rolls in when it changes. */
function Countdown({ startsAt, compact, urgent, C }) {
  const target = useMemo(() => new Date(startsAt).getTime(), [startsAt]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const left = Math.max(0, Math.floor((target - now) / 1000));
  const h = Math.floor(left / 3600), m = Math.floor((left % 3600) / 60), s = left % 60;
  const pad = (n) => String(n).padStart(2, "0");
  const color = urgent ? GOLD : C.text;

  // A plain function, not a component. Declared as `const Digits = () =>`
  // inside this render, it was a NEW component type every tick, so React
  // threw away every digit and remounted it, and all six replayed the roll
  // each second (Coleman, 2026-10-07: "the clock jumps"). Called as a
  // function, the keys hold and only a digit whose value changed rolls.
  const digits = (text, size, keyPrefix) => (
    <span style={{ display: "inline-flex", fontFamily: "'Bebas Neue',sans-serif", fontSize: size,
      lineHeight: 1, color, letterSpacing: 0.5, fontVariantNumeric: "tabular-nums" }}>
      {text.split("").map((ch, i) => (
        <span key={`${keyPrefix}${i}-${ch}`} className={ch === ":" ? undefined : "bam-cd-digit"}
          style={{ display: "inline-block", minWidth: ch === ":" ? "0.3em" : "0.52em", textAlign: "center",
            opacity: ch === ":" ? 0.55 : 1 }}>{ch}</span>
      ))}
    </span>
  );

  const unit = { fontSize: compact ? 8.5 : 9.5, fontWeight: 800, letterSpacing: 1.1,
    textTransform: "uppercase", color: C.textMid };

  if (compact) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
        {digits(h > 0 ? `${pad(h)}:${pad(m)}` : `${pad(m)}:${pad(s)}`, 28, "c")}
        <span style={unit}>{h > 0 ? "hrs  min" : "min  sec"}</span>
      </div>
    );
  }
  const parts = h > 0 ? [[pad(h), "hrs"], [pad(m), "min"], [pad(s), "sec"]] : [[pad(m), "min"], [pad(s), "sec"]];
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
      {parts.map(([v, label], i) => (
        <React.Fragment key={label}>
          {i > 0 && digits(":", 46, `sep${i}`)}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            {digits(v, 46, label)}
            <span style={unit}>{label}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

/** Counts up from zero the first time it is shown. */
function CountUp({ to, compact, C }) {
  const target = Number(to) || 0;
  const [n, setN] = useState(() => (reducedMotion() ? target : 0));
  useEffect(() => {
    if (reducedMotion()) { setN(target); return; }
    let raf, start;
    const dur = 700;
    const step = (t) => {
      if (start == null) start = t;
      const k = Math.min(1, (t - start) / dur);
      setN(Math.round(target * (1 - Math.pow(1 - k, 3))));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return (
    <span style={{ fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 0.5, lineHeight: 1,
      fontSize: compact ? 32 : 64, color: C.text, fontVariantNumeric: "tabular-nums" }}>{n}</span>
  );
}

/** Fills from empty when it appears. */
function Ring({ pct, big, small, size, C, dark }) {
  const stroke = size > 100 ? 6 : 4.5;
  const r = (size - stroke) / 2 - 1;
  const circ = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(1, Number(pct) || 0));
  const [shown, setShown] = useState(() => (reducedMotion() ? p : 0));
  useEffect(() => {
    const t = requestAnimationFrame(() => setShown(p));
    return () => cancelAnimationFrame(t);
  }, [p]);
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true"
        style={{ transform: "rotate(-90deg)", display: "block" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke}
          stroke={dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}/>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke}
          strokeLinecap="round" stroke={C.text}
          strokeDasharray={`${circ * shown} ${circ}`}
          style={{ transition: `stroke-dasharray .9s ${EASE}` }}/>
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", lineHeight: 1 }}>
        <span style={{ fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 0.5,
          fontSize: size > 100 ? 30 : 19, color: C.text }}>{big}</span>
        {size > 100 && small && (
          <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 1.1, textTransform: "uppercase",
            color: C.textMid, marginTop: 4 }}>{small}</span>
        )}
      </div>
    </div>
  );
}

/** A real cover. Advances through its sources on error, and gives up quietly. */
function Cover({ srcs, compact, progress, C }) {
  const list = useMemo(() => (Array.isArray(srcs) ? srcs : [srcs]).filter(Boolean), [srcs]);
  const [i, setI] = useState(0);
  useEffect(() => setI(0), [list]);
  if (i >= list.length) return null;
  const w = compact ? 96 : 280, h = compact ? 72 : "100%";
  return (
    <div className="bam-nextup-cover" style={{ position: "relative", width: w, height: h,
      minHeight: compact ? 72 : 168, flexShrink: 0, overflow: "hidden",
      borderRadius: compact ? R.lg : 0 }}>
      <img src={list[i]} alt="" aria-hidden="true" onError={() => setI(n => n + 1)}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }}/>
      {progress && (
        <div style={{ position: "absolute", left: 0, right: 0, bottom: 0,
          padding: compact ? "0 6px 6px" : "0 12px 12px",
          background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.7) 100%)" }}>
          {!compact && progress.label && (
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.1, textTransform: "uppercase",
              color: "rgba(255,255,255,0.85)", margin: "18px 0 6px" }}>{progress.label}</div>
          )}
          <div style={{ height: 3, borderRadius: 2, background: "rgba(255,255,255,0.22)", overflow: "hidden",
            marginTop: compact ? 14 : 0 }}>
            <div className="bam-fill" style={{ height: 3, width: `${Math.round(Math.max(0, Math.min(1, progress.pct)) * 100)}%`,
              background: "#FFFFFF", borderRadius: 2 }}/>
          </div>
        </div>
      )}
    </div>
  );
}

/** Whether a card has anything real to put on its face. */
function hasFace(action) {
  const f = action && action.face;
  const art = action && action.art;
  return !!((Array.isArray(art) ? art.length : art) ||
    (action.kind === "call" && action.startsAt) ||
    (f && (f.type === "ring" || f.type === "count")));
}

function Face({ action, compact, C, dark }) {
  const f = action.face;
  const panel = {
    width: compact ? 88 : 200, minHeight: compact ? 72 : 168, flexShrink: 0,
    borderRadius: compact ? R.lg : 0,
    display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column",
    background: dark ? "#111111" : "#F6F5F1",
    ...(compact ? { border: `1px solid ${C.border}` } : { borderRight: `1px solid ${C.border}` }),
  };

  if (action.art && (Array.isArray(action.art) ? action.art.length : true)) {
    const progress = f && f.type === "ring"
      ? { pct: f.pct, label: f.small && f.big ? `${f.big} ${f.small}` : null } : null;
    return <Cover srcs={action.art} compact={compact} progress={progress} C={C}/>;
  }
  if (action.kind === "call" && action.startsAt) {
    return (
      <div aria-hidden="true" style={{ ...panel, width: compact ? 96 : 240 }}>
        <Countdown startsAt={action.startsAt} compact={compact} urgent={action.urgent} C={C}/>
      </div>
    );
  }
  if (f && f.type === "ring") {
    return (
      <div aria-hidden="true" style={panel}>
        <Ring pct={f.pct} big={f.big} small={f.small} size={compact ? 58 : 118} C={C} dark={dark}/>
      </div>
    );
  }
  if (f && f.type === "count") {
    return (
      <div aria-hidden="true" style={panel}>
        <CountUp to={f.big} compact={compact} C={C}/>
        {f.small && (
          <span style={{ fontSize: compact ? 8.5 : 10, fontWeight: 800, letterSpacing: 1.1,
            textTransform: "uppercase", color: C.textMid, marginTop: compact ? 3 : 6 }}>{f.small}</span>
        )}
      </div>
    );
  }
  return null;
}

/* ── The stack ────────────────────────────────────────────────────────── */

/**
 * `renderFront(action)` may return a card of its own for the front of the
 * stack (this week's drop draws the drop card there); the rest of the stack
 * tucks under it as usual. Return null for the standard card.
 */
export function NextUp({ actions, C, dark, onGo, renderFront, compact = false }) {
  const list = useMemo(() => actions || [], [actions]);

  /* Which card is in front, by id, so a card arriving or leaving underneath
     does not shuffle what the coach is looking at. */
  const [frontId, setFrontId] = useState(null);
  const [dir, setDir] = useState("next");
  const [moved, setMoved] = useState(false);
  const ordered = useMemo(() => {
    const i = list.findIndex(a => a.id === frontId);
    return i > 0 ? [...list.slice(i), ...list.slice(0, i)] : list;
  }, [list, frontId]);
  useEffect(() => {
    if (frontId && !list.some(a => a.id === frontId)) setFrontId(null);
  }, [list, frontId]);

  const top = ordered[0] || null;
  const behind = ordered.slice(1);
  const many = ordered.length > 1;

  const goTo = useCallback((id, direction) => {
    haptic("selection");
    setDir(direction);
    setMoved(true);
    setFrontId(id);
  }, []);
  const next = useCallback(() => { if (many) goTo(ordered[1].id, "next"); }, [many, ordered, goTo]);
  const back = useCallback(() => { if (many) goTo(ordered[ordered.length - 1].id, "back"); }, [many, ordered, goTo]);

  const boxRef = useRef(null);
  const startX = useRef(null);
  const pointerId = useRef(null);
  const captured = useRef(false);
  const swallow = useRef(false);
  const [drag, setDrag] = useState(0);
  const [dragging, setDragging] = useState(false);

  const down = (e) => {
    if (!many) return;
    if (e.pointerType === "mouse") return;      // the strips and arrows are the mouse's way through
    if (!e.isPrimary) return;
    if (e.target && typeof e.target.closest === "function" &&
        e.target.closest("button, a, input, textarea")) return;
    startX.current = e.clientX;
    pointerId.current = e.pointerId;
    captured.current = false;
    swallow.current = false;
  };

  const move = (e) => {
    if (startX.current == null || e.pointerId !== pointerId.current) return;
    const dx = e.clientX - startX.current;
    if (!captured.current) {
      if (Math.abs(dx) < DRAG_START) return;
      captured.current = true;
      swallow.current = true;
      setDragging(true);
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* not supported */ }
    }
    setDrag(dx);
  };

  const up = (e) => {
    if (startX.current == null) return;
    if (e && e.pointerId != null && e.pointerId !== pointerId.current) return;
    const w = boxRef.current ? boxRef.current.offsetWidth : 360;
    const d = drag;
    const wasDrag = captured.current;
    startX.current = null;
    pointerId.current = null;
    captured.current = false;
    setDragging(false);
    setDrag(0);
    if (!wasDrag) return;
    if (d < -w * SWIPE_FRACTION) next();
    else if (d > w * SWIPE_FRACTION) back();
  };

  // A drag that ends over the button is not a press of that button.
  const onClickCapture = (e) => {
    if (swallow.current) { swallow.current = false; e.stopPropagation(); e.preventDefault(); }
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") { e.preventDefault(); next(); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); back(); }
  };

  if (!top) return null;

  const urgent = !!top.urgent;
  const custom = renderFront ? renderFront(top) : null;

  const cta = (
    <button type="button" onClick={() => { haptic("light"); onGo && onGo(top); }}
      className="bam-nextup-cta"
      style={{
        flexShrink: 0,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        height: compact ? 44 : 50, padding: compact ? "0 22px" : "0 28px",
        borderRadius: R.pill, border: "1px solid transparent",
        background: GOLD, color: "#111111",
        fontSize: compact ? 14 : 15, fontWeight: 800, fontFamily: "inherit",
        cursor: "pointer", whiteSpace: "nowrap",
      }}>
      {top.cta}
    </button>
  );

  const backBtn = moved && many && (
    <button type="button" onClick={back} aria-label="Back" title="Back"
      className="btn-inline bam-nextup-back"
      style={{
        position: "absolute", top: compact ? 10 : 12, right: (compact ? 10 : 12) + (custom ? 42 : 0), zIndex: 4,
        width: 34, height: 34, borderRadius: R.pill,
        border: `1px solid ${C.border}`, background: dark ? "rgba(20,20,20,0.85)" : "rgba(255,255,255,0.9)",
        color: C.textMid, cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
      <ArrowLeft size={15} strokeWidth={2.3}/>
    </button>
  );

  const eyebrow = (
    <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: compact ? 10 : 10.5,
      fontWeight: 800, letterSpacing: compact ? 1.4 : 1.5, textTransform: "uppercase",
      color: urgent ? GOLD : C.textMid, paddingRight: backBtn && compact ? 36 : 0 }}>
      {urgent && <span className="bam-nextup-live" aria-hidden="true"/>}
      {top.eyebrow}
    </div>
  );

  const face = hasFace(top) ? <Face action={top} compact={compact} C={C} dark={dark}/> : null;
  const strips = behind.slice(0, STRIPS);
  const more = behind.length - strips.length;
  const enter = reducedMotion() ? "" : (dir === "back" ? "bam-nextup-in-back" : "bam-nextup-in-next");

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <style>{`
        .bam-nextup{transition:transform .35s ${EASE},box-shadow .35s ${EASE};}
        @media (hover:hover){
          .bam-nextup:hover{transform:translateY(-2px);box-shadow:0 18px 44px rgba(0,0,0,${dark ? 0.5 : 0.12})!important;}
          .bam-nextup-strip:hover{filter:brightness(${dark ? 1.35 : 0.97});}
          .bam-nextup-strip:hover .bam-nextup-next svg{transform:translateX(3px);}
          .bam-nextup-cta:hover{filter:brightness(1.06);transform:translateY(-1px);}
          .bam-nextup-back:hover{color:${C.text}!important;}
          .bam-nextup:hover .bam-nextup-cover img{transform:scale(1.04);}
        }
        .bam-nextup-strip{transition:filter .25s ${EASE};}
        .bam-nextup-next svg,.bam-nextup-cta,.bam-nextup-back{transition:transform .25s ${EASE},filter .25s ${EASE},color .2s ${EASE};}
        .bam-nextup-cover img{transition:transform 1.2s ${EASE};}
        .bam-nextup-in-next{animation:bamNextIn .42s ${EASE} both;}
        .bam-nextup-in-back{animation:bamBackIn .42s ${EASE} both;}
        @keyframes bamNextIn{from{opacity:0;transform:translateX(28px)}to{opacity:1;transform:none}}
        @keyframes bamBackIn{from{opacity:0;transform:translateX(-28px)}to{opacity:1;transform:none}}
        .bam-strip-in{animation:bamStripIn .4s ${EASE} both;}
        @keyframes bamStripIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:none}}
        .bam-nextup-live{width:7px;height:7px;border-radius:50%;background:${GOLD};flex-shrink:0;
          animation:bamNextupLive 1.6s ease-out infinite;}
        @keyframes bamNextupLive{0%{box-shadow:0 0 0 0 ${GOLD}88}70%{box-shadow:0 0 0 7px ${GOLD}00}100%{box-shadow:0 0 0 0 ${GOLD}00}}
        .bam-cd-digit{animation:bamDigit .45s ${EASE} both;}
        @keyframes bamDigit{from{opacity:.2;transform:translateY(-22%)}to{opacity:1;transform:none}}
        @media (prefers-reduced-motion: reduce){
          .bam-nextup,.bam-nextup-strip,.bam-nextup-cover img{transition:none}
          .bam-nextup-live,.bam-cd-digit,.bam-nextup-in-next,.bam-nextup-in-back,.bam-strip-in{animation:none}
        }
      `}</style>

      <div
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        onLostPointerCapture={up}
        onClickCapture={onClickCapture}
        onKeyDown={onKeyDown}
        tabIndex={many ? 0 : undefined}
        aria-roledescription={many ? "stack" : undefined}
        className={custom ? undefined : "bam-nextup"}
        style={{
          position: "relative", zIndex: 3,
          borderRadius: R.xl,
          overflow: custom ? "visible" : "hidden",
          outline: "none",
          ...(custom ? {} : {
            background: dark ? "#141414" : "#FFFFFF",
            border: urgent ? `1px solid ${GOLD}` : `1px solid ${C.border}`,
            boxShadow: urgent
              ? `0 0 0 3px ${GOLD}1F, ${dark ? "0 10px 40px rgba(0,0,0,0.45)" : "0 4px 18px rgba(0,0,0,0.08)"}`
              : (dark ? "0 10px 40px rgba(0,0,0,0.45)" : "0 4px 18px rgba(0,0,0,0.08)"),
          }),
          // Inline only while a finger is on it, so the stylesheet's hover
          // and ease transitions apply the rest of the time.
          ...(dragging ? { transform: `translateX(${drag}px) rotate(${drag * 0.012}deg)`, transition: "none" } : {}),
          touchAction: many ? "pan-y" : "auto",
        }}
      >
        {backBtn}
        <div key={top.id} className={enter}>
          {custom || (compact ? (
            <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", gap: 13, alignItems: "flex-start" }}>
                {face}
                <div style={{ flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
                  {eyebrow}
                  <div style={{ fontSize: 16.5, fontWeight: 800, color: C.text, lineHeight: 1.22 }}>{top.title}</div>
                  {top.why && <div style={{ fontSize: 13, color: C.textMid, lineHeight: 1.4 }}>{top.why}</div>}
                </div>
              </div>
              <div style={{ display: "flex" }}>{cta}</div>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "stretch", minHeight: face ? 168 : 0 }}>
              {face}
              <div style={{ flexGrow: 1, minWidth: 0, display: "flex", alignItems: "center",
                gap: 28, padding: face ? "22px 26px" : "26px 30px", paddingRight: backBtn ? 64 : (face ? 26 : 30) }}>
                <div style={{ flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 7 }}>
                  {eyebrow}
                  <div style={{ fontSize: 26, fontWeight: 800, color: C.text,
                    letterSpacing: -0.5, lineHeight: 1.15 }}>{top.title}</div>
                  {top.why && <div style={{ fontSize: 14.5, color: C.textMid, lineHeight: 1.45 }}>{top.why}</div>}
                </div>
                {cta}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* The rest of the stack, tucked under the card's bottom edge with
          their titles showing. The first is Next. */}
      {strips.map((a, i) => {
        const Icon = KIND_ICON[a.kind] || Sparkles;
        const inset = (i + 1) * (compact ? 8 : 14);
        const isNext = i === 0;
        const last = i === strips.length - 1;
        return (
          <button key={a.id} type="button" onClick={() => goTo(a.id, "next")}
            aria-label={isNext ? `Next: ${a.title}` : `Show: ${a.title}`}
            className="bam-nextup-strip bam-strip-in"
            style={{
              position: "relative", zIndex: 2 - i,
              display: "flex", alignItems: "center", gap: 10,
              width: `calc(100% - ${inset * 2}px)`, margin: `-${R.xl}px ${inset}px 0`,
              padding: `${R.xl + (compact ? 9 : 10)}px ${compact ? 14 : 18}px ${compact ? 9 : 10}px`,
              borderRadius: `0 0 ${R.xl}px ${R.xl}px`,
              border: `1px solid ${C.border}`, borderTop: "none",
              background: dark ? (i === 0 ? "#101010" : "#0D0D0D") : (i === 0 ? "#F7F6F2" : "#F1EFEA"),
              color: C.textMid, cursor: "pointer", fontFamily: "inherit", textAlign: "left",
              animationDelay: `${60 + i * 50}ms`,
            }}>
            {a.urgent
              ? <span className="bam-nextup-live" aria-hidden="true"/>
              : <Icon size={14} color={C.textMid} strokeWidth={2} style={{ flexShrink: 0 }}/>}
            <span style={{ fontSize: compact ? 12.5 : 13, fontWeight: 700, color: i === 0 ? C.text : C.textMid,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: 1 }}>
              {a.title}
            </span>
            {last && more > 0 && (
              <span style={{ fontSize: 11, color: C.textMid, flexShrink: 0, whiteSpace: "nowrap" }}>+{more} more</span>
            )}
            {isNext && (
              <span className="bam-nextup-next" style={{ display: "inline-flex", alignItems: "center", gap: 5, flexShrink: 0,
                fontSize: 11, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase", color: C.text }}>
                Next <ArrowRight size={14} strokeWidth={2.4}/>
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default NextUp;
